'use client'

import React, { createContext, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'

import { Session } from '../../types'
import { signOut } from './client-signout'

export interface SessionContextValue {
  session?: Session
  build?: ReturnType<typeof getBuildProperties>
  isLoading: boolean
  signOut: typeof signOut
  setTrigger: (value: boolean) => void
}

interface SessionResponse {
  session: Session
  build: ReturnType<typeof getBuildProperties>
}

interface SessionProviderProps {
  children: React.ReactNode
}

// Add as needed from: https://swr.vercel.app/docs/api
interface SWROptions {
  refreshInterval?: number
}

export const SessionContext = createContext<SessionContextValue | undefined>(
  undefined,
)

export const getBuildProperties = () => {
  return {
    hash: process.env.GIT_HASH ?? 'unknown',
    date: process.env.BUILD_DATE ?? 'unknown',
    version: process.env.BUILD_VERSION ?? 'unknown',
  }
}

const fetcher = async (url: string): Promise<SessionResponse> => {
  const res = await fetch(url)
  const data = await res.json()
  return { session: data.session, build: data.build }
}

export const useRawSessionFetcher = (options?: SWROptions) => {
  return useSWR<SessionResponse>('/api/auth/session', fetcher, options)
}

export const SessionProvider = ({ children }: SessionProviderProps) => {
  const [trigger, setTrigger] = useState<boolean>(true)

  const [sessionData, setSessionData] = useState<SessionContextValue>({
    isLoading: true,
    signOut,
    setTrigger,
  })

  useEffect(() => {
    if (trigger) {
      setSessionData((prev) => ({
        ...prev,
        isLoading: true,
      }))

      fetcher('/api/auth/session').then((response) => {
        setSessionData((prev) => ({
          ...prev,
          ...response,
          isLoading: false,
        }))
        setTrigger(false)
      })
    }
  }, [trigger])

  return (
    <SessionContext.Provider value={sessionData}>
      {children}
    </SessionContext.Provider>
  )
}
